import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import STRINGS from 'constants/strings';
import {
    FilterBrands,
    RadioButtonFilter,
    Accordion,
    AccordionItem,
    PriceRange,
    SortRadioButtons,
    Spinner
} from 'components';
import { formatNumber } from 'packages';
import CONSTANTS from '../constants/constants';
import ItemLayoutSwitch from '../layout/item-layout-switch';
import RadioSwitchFilter from './radio-switch-filter';
import * as gtm from '../search-gtm';

const getSelectedValue = (filter = {}) => {
    const { selectedValue } = filter;
    if (!selectedValue) {
        return '';
    }
    const selected = filter.options.find(option => {
        return option.value === selectedValue;
    });
    const activeFilter = CONSTANTS.ACTIVE_FILTER_STATES.includes(
        selected.value
    );
    return (
        <span className={`${activeFilter ? 'text-primary' : ''} mr-3`}>
            {selected.name}
        </span>
    );
};

const SearchFilterModal = ({
    searchResults,
    handleFilterChange,
    handleBrandChange,
    handlePriceFilterChange,
    itemLayout,
    dispatch,
    handleClearAllFilters,
    queryParams,
    pageType,
    isUpdating
}) => {
    const {
        filters,
        filterBrands = {},
        filterPrice = {},
        totalItems,
        activeFilters
    } = searchResults;
    const {
        options: brands,
        selectedValues: selectedBrands = []
    } = filterBrands;
    const {
        itemCondition,
        sellerType,
        itemStatus,
        county,
        itemType,
        sortBy
    } = filters;
    const { fromPrice, toPrice } = filterPrice;
    const userSetPriceFilter = fromPrice.value > 1 || toPrice.value < 5000;
    const totalActiveFilters =
        activeFilters.length + selectedBrands.length + userSetPriceFilter;
    return (
        <Fragment>
            <div className="fixed-top">
                <div className="search-modal__header d-flex justify-content-center align-items-center">
                    <strong>Filtrera</strong>
                    <strong
                        className="clear-all--modal text-danger"
                        onClick={() => {
                            gtm.trackAction('Filter menu', 'Reset');
                            handleClearAllFilters();
                        }}>
                        {STRINGS.DISCOVER.FILTERS.CLEAR}
                        {totalActiveFilters > 0 && (
                            <span className="d-inline-block badge badge-pill badge-primary ml-2">
                                {totalActiveFilters}
                            </span>
                        )}
                    </strong>
                </div>
                <div className="search-modal__hits d-flex align-items-center justify-content-center">
                    <span>{`Hittade ${formatNumber(
                        totalItems
                    )} annonser`}</span>
                </div>
            </div>
            {isUpdating && <Spinner className="filters--updating" />}
            <Accordion>
                <AccordionItem
                    hideTitle={true}
                    defaultOpen
                    collapsable={false}
                    render={() => (
                        <div className="d-flex justify-content-between">
                            <span className="align-self-center mr-5">
                                Visa som:
                            </span>
                            <ItemLayoutSwitch
                                dispatch={dispatch}
                                selectedLayout={itemLayout}
                                cssClass={'flex-grow-1'}
                                id="filter-modal-itemLayout"
                            />
                        </div>
                    )}
                />
                <AccordionItem
                    title={STRINGS.DISCOVER.FILTERS.SORTING}
                    onChange={() => gtm.trackAccordion('sortBy')}
                    defaultOpen={false}
                    label={getSelectedValue(sortBy)}
                    render={() => (
                        <SortRadioButtons
                            sortBy={sortBy}
                            handleFilterChange={handleFilterChange}
                            id="filter-modal-sortBy"
                        />
                    )}
                />
                {!brands || brands.length < 1 ? (
                    <div className="d-none" />
                ) : (
                    <AccordionItem
                        title={STRINGS.DISCOVER.FILTERS.BRANDS}
                        onChange={() => gtm.trackAccordion('brandTags')}
                        defaultOpen={false}
                        label={
                            selectedBrands.length > 0 && (
                                <span className="badge badge-pill badge-primary">
                                    {selectedBrands.length}
                                </span>
                            )
                        }
                        render={() => (
                            <FilterBrands
                                filterBrands={filterBrands}
                                handleBrandChange={handleBrandChange}
                                queryParams={queryParams}
                                pageType={pageType}
                            />
                        )}
                    />
                )}
                <AccordionItem
                    title={STRINGS.DISCOVER.FILTERS.CONDITION}
                    onChange={() => gtm.trackAccordion('itemCondition')}
                    defaultOpen={false}
                    label={getSelectedValue(itemCondition)}
                    render={() => (
                        <RadioSwitchFilter
                            filter={itemCondition}
                            handleFilterChange={handleFilterChange}
                            id="filter-modal-itemCondition"
                        />
                    )}
                />
                <AccordionItem
                    title={STRINGS.DISCOVER.FILTERS.ITEM_TYPE}
                    onChange={() => gtm.trackAccordion('itemType')}
                    defaultOpen={false}
                    label={getSelectedValue(itemType)}
                    render={() => (
                        <RadioSwitchFilter
                            filter={itemType}
                            handleFilterChange={handleFilterChange}
                            id="filter-modal-itemType"
                        />
                    )}
                />
                <AccordionItem
                    title={STRINGS.DISCOVER.FILTERS.PRICE}
                    onChange={() => gtm.trackAccordion('price')}
                    defaultOpen={true}
                    collapsable={false}
                    render={() => (
                        <PriceRange
                            rangeStart={1}
                            rangeEnd={5000}
                            handleStart={filterPrice['fromPrice'].value}
                            handleEnd={filterPrice['toPrice'].value}
                            handlePriceFilterChange={handlePriceFilterChange}
                        />
                    )}
                />
                <AccordionItem
                    title={STRINGS.DISCOVER.FILTERS.SELLER_TYPE}
                    onChange={() => gtm.trackAccordion('sellerType')}
                    defaultOpen={false}
                    label={getSelectedValue(sellerType)}
                    render={() => (
                        <RadioSwitchFilter
                            filter={sellerType}
                            handleFilterChange={handleFilterChange}
                            id="filter-modal-sellerType"
                        />
                    )}
                />
                <AccordionItem
                    title={STRINGS.DISCOVER.FILTERS.COUNTY}
                    onChange={() => gtm.trackAccordion('county')}
                    defaultOpen={false}
                    label={getSelectedValue(county)}
                    render={() => (
                        <RadioButtonFilter
                            filter={county}
                            handleFilterChange={handleFilterChange}
                            id="filter-modal-county"
                        />
                    )}
                />
                <AccordionItem
                    title={STRINGS.DISCOVER.FILTERS.ITEM_STATUS}
                    onChange={() => gtm.trackAccordion('itemStatus')}
                    defaultOpen={false}
                    label={getSelectedValue(itemStatus)}
                    render={() => (
                        <RadioSwitchFilter
                            filter={itemStatus}
                            handleFilterChange={handleFilterChange}
                            id="filter-modal-itemStatus"
                        />
                    )}
                />
            </Accordion>
        </Fragment>
    );
};

SearchFilterModal.propTypes = {
    searchResults: PropTypes.object,
    handleFilterChange: PropTypes.func
};

export default SearchFilterModal;



// WEBPACK FOOTER //
// ./src/EbaySweden.TouchWeb/static/script/components/search-results/filters/search-filter-modal.jsx